// Adapted from T3 Code packages/shared/src/remote.ts; MIT, docs/licenses/T3-Code-MIT.txt.

/**
 * Pairing links: how one machine hands another a way in, and how the receiving side reads it.
 *
 * A link is an address the Environment answers at plus a one-time Environment Access grant, as
 * `<endpoint>/pair#token=<grant>`. The grant rides in the fragment so it never reaches a server log
 * or a proxy. The address alone grants nothing; the grant is spent on first use.
 *
 * @module remotePairing
 */
import * as Data from "effect/Data";

const PAIRING_TOKEN_PARAM = "token";
const HOSTED_PAIRING_HOST_PARAM = "host";
const SUPPORTED_PROTOCOLS = new Set(["http:", "https:", "ws:", "wss:"]);

/** A pairing input that names no usable address or carries no grant. The message is the UI's. */
export class RemotePairingInputError extends Data.TaggedError("RemotePairingInputError")<{
  readonly message: string;
}> {}

export interface ResolvedRemotePairingTarget {
  readonly credential: string;
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
}

const readHashParams = (url: URL): URLSearchParams =>
  new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);

export const getPairingTokenFromUrl = (url: URL): string | null => {
  const hashToken = readHashParams(url).get(PAIRING_TOKEN_PARAM)?.trim() ?? "";
  if (hashToken.length > 0) return hashToken;
  const searchToken = url.searchParams.get(PAIRING_TOKEN_PARAM)?.trim() ?? "";
  return searchToken.length > 0 ? searchToken : null;
};

export const setPairingTokenOnUrl = (url: URL, credential: string): URL => {
  const next = new URL(url.toString());
  next.searchParams.delete(PAIRING_TOKEN_PARAM);
  next.hash = new URLSearchParams([[PAIRING_TOKEN_PARAM, credential]]).toString();
  return next;
};

/** The link another machine is handed: `<endpoint>/pair#token=<grant>`. */
export const buildPairingUrl = (endpointUrl: string, credential: string): string => {
  const url = new URL(endpointUrl);
  url.pathname = "/pair";
  url.search = "";
  return setPairingTokenOnUrl(url, credential).toString();
};

const withBaseProtocol = (url: URL, protocol: "http" | "ws"): string => {
  const next = new URL(url.toString());
  const secure = next.protocol === "https:" || next.protocol === "wss:";
  next.protocol = protocol === "http" ? (secure ? "https:" : "http:") : secure ? "wss:" : "ws:";
  next.pathname = "/";
  next.search = "";
  next.hash = "";
  return next.toString();
};

/** A host as someone types it: no scheme means HTTPS, which is what Tailscale Serve answers on. */
const normalizeHost = (rawValue: string): URL => {
  const trimmed = rawValue.trim().replace(/^\/+/, "");
  if (!trimmed) throw new RemotePairingInputError({ message: "Enter a machine address." });
  const withScheme = /^[a-zA-Z][a-zA-Z\d+-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    throw new RemotePairingInputError({ message: "That machine address is not valid." });
  }
  if (!SUPPORTED_PROTOCOLS.has(url.protocol)) {
    throw new RemotePairingInputError({ message: "That machine address is not valid." });
  }
  return url;
};

/**
 * Reads what a person pasted or typed: a full pairing link, or an address and a code.
 *
 * A link whose query names a `host` is a link to another page that carries this machine's address
 * inside it, and the address it carries is the one reached.
 */
export const resolveRemotePairingTarget = (input: {
  readonly pairingUrl?: string;
  readonly host?: string;
  readonly pairingCode?: string;
}): ResolvedRemotePairingTarget => {
  const pairingUrl = input.pairingUrl?.trim() ?? "";
  if (pairingUrl.length > 0) {
    let url: URL;
    try {
      url = new URL(pairingUrl);
    } catch {
      throw new RemotePairingInputError({ message: "That pairing link is not valid." });
    }
    if (!SUPPORTED_PROTOCOLS.has(url.protocol)) {
      throw new RemotePairingInputError({ message: "That pairing link is not valid." });
    }
    const credential = getPairingTokenFromUrl(url);
    if (credential === null) {
      throw new RemotePairingInputError({ message: "That pairing link has no code in it." });
    }
    const hostedHost = url.searchParams.get(HOSTED_PAIRING_HOST_PARAM)?.trim();
    const base = hostedHost ? normalizeHost(hostedHost) : url;
    return {
      credential,
      httpBaseUrl: withBaseProtocol(base, "http"),
      wsBaseUrl: withBaseProtocol(base, "ws"),
    };
  }
  const base = normalizeHost(input.host ?? "");
  const credential = input.pairingCode?.trim() ?? "";
  if (!credential) throw new RemotePairingInputError({ message: "Enter a pairing code." });
  return {
    credential,
    httpBaseUrl: withBaseProtocol(base, "http"),
    wsBaseUrl: withBaseProtocol(base, "ws"),
  };
};
